import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import {
  ALL_PAGES,
  NAV_ITEMS,
  groupLanding,
  groupOf,
  parsePath,
  pathFor,
  type NavItem,
  type PageId,
  type Route,
} from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { MemberAvatar } from "@/components/member-avatar";

import { TodayPage } from "@/pages/today";
import { CalendarPage } from "@/pages/calendar";
import { BillsPage } from "@/pages/bills";
import { ShoppingPage } from "@/pages/shopping";
import { FinanceDashboardPage } from "@/pages/finance-dashboard";
import { FinanceBudgetPage } from "@/pages/finance-budget";
import { FinanceConfigPage } from "@/pages/finance-config";
import { SettingsPage } from "@/pages/settings";

// ----- Navigation context (lets pages jump between tabs) -------------------

/** Navigate to a page, optionally addressing one thing (a budget id). */
type NavigateFn = (page: PageId, param?: string) => void;

const NavContext = createContext<NavigateFn>(() => {});
export const useNavigate = () => useContext(NavContext);

const RouteContext = createContext<Route>({ page: "today" });
/** The current route, including the id in the URL where there is one. */
export const useRoute = () => useContext(RouteContext);

// ----- Shell ---------------------------------------------------------------

const PAGES: Record<PageId, React.ComponentType> = {
  today: TodayPage,
  calendar: CalendarPage,
  bills: BillsPage,
  shopping: ShoppingPage,
  "finance-dashboard": FinanceDashboardPage,
  "finance-config": FinanceConfigPage,
  "finance-budget": FinanceBudgetPage,
  settings: SettingsPage,
};

export function AppShell() {
  // The URL is the source of truth, so every view can be linked to, reloaded
  // and reached with the browser's back button.
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname));
  const page = route.page;

  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const setPage = useCallback<NavigateFn>((next, param) => {
    const path = pathFor(next, param);
    if (path !== window.location.pathname) {
      window.history.pushState({}, "", path);
    }
    setRoute({ page: next, param });
  }, []);

  const { household, user, members } = useAuth();
  const Page = PAGES[page];

  // Grouped pages read as "Finance · Configuration" in the header.
  const group = groupOf(page);
  const pageLabel = ALL_PAGES.find((n) => n.id === page)?.label ?? "";
  const activeLabel = group ? `${group.label} · ${pageLabel}` : pageLabel;

  // A group expands when one of its pages becomes active, and can be collapsed
  // by tapping its header.
  const [openGroup, setOpenGroup] = useState<string | null>(group?.label ?? null);
  useEffect(() => {
    if (group) setOpenGroup(group.label);
  }, [group?.label]);

  return (
    <NavContext.Provider value={setPage}>
      <RouteContext.Provider value={route}>
      <div className="min-h-screen bg-muted/30">
        {/* Desktop sidebar */}
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r bg-sidebar md:flex">
          <div className="flex h-16 items-center gap-2 px-5">
            <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold">
              ♥
            </div>
            <div className="font-semibold leading-tight">
              {household?.name ?? "FamilyLife"}
              <div className="text-xs font-normal text-muted-foreground">FamilyLife</div>
            </div>
          </div>

          <nav className="flex-1 space-y-1 px-3 py-2">
            {NAV_ITEMS.map((item) => {
              if (!item.children) {
                return (
                  <NavButton
                    key={item.id}
                    active={page === item.id}
                    onClick={() => setPage(item.id!)}
                    icon={item.icon}
                    label={item.label}
                  />
                );
              }

              const open = openGroup === item.label;
              return (
                <div key={item.label}>
                  <NavButton
                    active={!open && group?.label === item.label}
                    onClick={() => setOpenGroup(open ? null : item.label)}
                    icon={item.icon}
                    label={item.label}
                    trailing={<ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />}
                  />
                  {open && (
                    <div className="mt-1 space-y-1 border-l pl-3 ml-5">
                      {item.children.filter((c) => !c.hidden).map((child) => (
                        <NavButton
                          key={child.id}
                          active={page === child.id}
                          onClick={() => setPage(child.id!)}
                          icon={child.icon}
                          label={child.label}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 border-t p-3">
            {user && <MemberAvatar member={user} />}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user?.name}</div>
              <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
            </div>
          </div>
        </aside>

        {/* Main column */}
        <div className="md:pl-64">
          {/* Top bar */}
          <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b bg-background/80 px-4 backdrop-blur md:px-8">
            <h1 className="text-lg font-semibold md:text-xl">{activeLabel}</h1>
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {members.map((m) => (
                  <MemberAvatar key={m.id} member={m} size="sm" className="ring-2 ring-background" />
                ))}
              </div>
            </div>
          </header>

          {/*
            Fluid: the column fills whatever width is available, so the budget
            grid can show all twelve months instead of scrolling inside a
            1024px cap. Pages that read better narrow (Settings) set their own
            width.
          */}
          <main className="w-full px-4 pb-24 pt-5 md:px-8 md:pb-10">
            <Page />
          </main>
        </div>

        {/* Mobile bottom tab bar */}
        <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-6 border-t bg-background/95 backdrop-blur md:hidden">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            // A group tab is active for any of its pages and opens the first one;
            // the in-page switcher moves between siblings from there.
            const landing = groupLanding(item)!;
            const active = item.children ? group?.label === item.label : page === item.id;
            return (
              <button
                key={item.id ?? item.label}
                onClick={() => setPage(landing)}
                className={cn(
                  "flex flex-col items-center gap-1 py-2 text-[11px] transition-colors",
                  active ? "text-primary" : "text-muted-foreground",
                )}
              >
                <Icon className={cn("size-5", active && "scale-110")} />
                {item.label}
              </button>
            );
          })}
        </nav>
        </div>
      </RouteContext.Provider>
    </NavContext.Provider>
  );
}

function NavButton({
  active,
  disabled,
  onClick,
  icon: Icon,
  label,
  badge,
  trailing,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  badge?: string;
  trailing?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        active && "bg-primary text-primary-foreground",
        !active && !disabled && "text-foreground hover:bg-accent",
        disabled && "cursor-not-allowed text-muted-foreground/60",
      )}
    >
      <Icon className="size-5" />
      <span className="flex-1 text-left">{label}</span>
      {badge && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{badge}</span>}
      {trailing}
    </button>
  );
}
